import { FC, ReactElement, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { TitleWelcomeResponseMessage } from '@/types/TitleWelcomeResponse';
import { Container, Typography, useMediaQuery, useTheme, Box, Skeleton, ButtonBase } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
// Removed VideoSubjectSkeleton import; replaced by AlbumSubjectSkeleton
import VideoSubject from 'components/VideoSubject';
import AlbumSubjectSkeleton from 'components/AlbumSubjectSkeleton';
import TitleHeader from 'components/TitleHeader';
import LanguageMenu from 'components/LanguageMenu';
import LanguageAvatar from 'components/LanguageAvatar';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useTitleWelcomeQuery} from 'hooks/apiHooks';
import useAuthRedirect from 'hooks/useAuthRedirect';
import { ownerPath, videoPath, classPath } from 'routes';
import OverflowFilterBar from 'components/OverflowFilterBar';
import { getTitleFromCode } from 'utils/ClassUtils';
import { NoSnippet } from 'utils/NoSnippet';

// Album row with scroll shadows and paging controls
// Extract element type of albumMetadata safely
type Album = NonNullable<TitleWelcomeResponseMessage['albumMetadata']>['albumMetadata'][number];
const AlbumRow: FC<{ album: Album; currentLang: string }> = ({ album, currentLang }) => {
  const theme = useTheme();
  // list of videos
  const fullVideos = album.videoMetadata?.videoMetadata ?? [];
  const albumLang = album.classCategory?.titleLingo ?? '';
  const showLangAvatar = albumLang !== '' && (currentLang !== 'en' || albumLang !== 'en');

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Box sx={{ px: { xs: 1, sm: 1, md: 0 }, mb: 0 }}>
        <ButtonBase
          component={Link}
          to={
            classPath(
              album.classCategory?.titleLingo ?? '',
              album.classCategory?.titleClass ?? ''
            )
          }
          sx={(theme) => ({
            width: '100%',
            borderRadius: 1,
            px: 0,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            '&:hover': { bgcolor: theme.palette.action.hover },
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden', gap: 1 }}>
            {showLangAvatar && (
              <LanguageAvatar
                language={albumLang}
                sx={{
                  width: theme.spacing(4),
                  height: theme.spacing(3),
                  borderRadius: 0,
                }}
              />
            )}
            <Typography
              variant="h5"
              component="h3"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
            {getTitleFromCode(album.classCategory?.titleClass ?? '')}
            </Typography>
          </Box>
          <ChevronRightIcon fontSize="small" color="action" />
        </ButtonBase>
      </Box>
      <Box>
        <Grid
          container
          wrap="nowrap"
          columnSpacing={{ xs: 1, sm: 2 }}
          sx={{
            scrollSnapType: 'x mandatory',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {fullVideos.map(video => (
            <Grid
              key={video.musicVideo}
              item
              xs={6}
              md={4}
              xl={3}
              sx={{
                flex: '0 0 auto',
                scrollSnapAlign: 'start',
                py: 1,
              }}
            >
              <VideoSubject
                video={video}
                videoLink={{
                  to: videoPath(video.musicVideo),
                  state: { facadeQueue: fullVideos, shadowQueue: fullVideos },
                }}
                ownerLink={{
                  to: ownerPath(video.musicOwner)
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

const TitleWelcome: FC = (): ReactElement => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // i18n translation and user language
  const { t, i18n } = useTranslation(['titleWelcome', 'titleToolbar']);
  // Current two-letter ISO language code from user settings
  const currentLang = (i18n.language || 'en').split('-')[0];
  const navigate = useNavigate();
  // Scroll to top when entering Welcome page
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch { /* ignore */ }
  }, []);
  // Dynamic head tags via React Helmet
  const { t: tPromo } = useTranslation('promoSubject');
  const headTitle = tPromo('titlePromo');
  const headDesc = tPromo('actorPromo');
  const headOgDesc = tPromo('coverPromo');
  const helmet = (
    <Helmet>
      <title>{headTitle}</title>
      <meta name="description" content={headDesc} />
      <meta property="og:title" content={headTitle} />
      <meta property="og:description" content={headOgDesc} />
    </Helmet>
  );
  // Use two-letter ISO language code for server requests
  const titleLingo = (i18n.language || 'en').split('-')[0];
  const { data, error, isLoading } = useTitleWelcomeQuery(titleLingo);
  useAuthRedirect(error, { replace: true });
  if (isLoading) {
    // Show header and album skeleton sections while loading
    const skeletonAlbumCount = 3;
    return (
      <>
        {helmet}
        <Container maxWidth="xl" disableGutters={isSmallScreen}>
        {/* Header skeleton */}
        <Box sx={{ px: { sm: 2 }, mb: 2 }}>
          <Skeleton variant="rectangular" width="100%" height={theme.spacing(17)} />
        </Box>
        {/* Album skeletons */}
        {Array.from({ length: skeletonAlbumCount }).map((_, idx) => (
          <AlbumSubjectSkeleton key={idx} />
        ))}
        </Container>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        {helmet}
        <Typography color="error">{t('errorLoading')}</Typography>
      </>
    );
  }
  // (AlbumRow handles per-album video selection and owner navigation)
  return (
    <>
      {helmet}
      <Container maxWidth="xl" disableGutters={isSmallScreen}>
      <Box sx={{ mb: 2 }}>
        <TitleHeader
          title={t('welcome')}
          subtitle={t('welcomeSubtitle')}
          actions={
            <LanguageMenu
              sx={{
                color: '#FFFFFF',
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
              }}
            />
          }
          waveform
        />
      </Box>
      {/* Render each album section */}
      {data.albumMetadata?.albumMetadata.map((album, idx) => (
        <AlbumRow key={idx} album={album} currentLang={currentLang} />
      ))}
      {/* Explore additional classes */}
      <NoSnippet>
      {data.classMetadata?.classMetadata
          && data.classMetadata?.classMetadata.length > 0 && (
        <Box sx={{ px: { xs: 1, sm: 2 }, mt: 4, mb: 4 }}>
          <OverflowFilterBar
            filters={data.classMetadata?.classMetadata}
            selectedFilter={null}
            onFilterSelect={c => {
              if (c) {
                navigate(classPath(c.titleLingo, c.titleClass));
              }
            }}
            containerSx={{ px: 0 }}
            itemSx={{
              flex: '0 0 auto',
              textTransform: 'uppercase',
              px: 2,
              py: 1,
              borderRadius: 1,
              bgcolor: 'grey.100',
              color: 'text.primary',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'grey.200' },
            }}
            moreSx={{
              flex: '0 0 auto',
              px: 2,
              py: 1,
              borderRadius: 1,
              bgcolor: 'grey.100',
              color: 'text.primary',
              '&:hover': { bgcolor: 'grey.200' },
            }}
            itemIcon={<ChevronRightIcon sx={{ ml: 0.5, fontSize: '0.875rem' }} />}
          />
        </Box>
      )}
      </NoSnippet>
    </Container>
  </>
  );
};

export default TitleWelcome;